﻿import { useTranslation } from "react-i18next";
import TopBar from "@/pages/home/components/TopBar";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import StickyMobile from "@/pages/home/components/StickyMobile";

function Breadcrumb() {
  return (
    <nav aria-label="Kruimelpad" className="bg-white border-b border-brand-border">
      <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-3">
        <ol className="flex items-center gap-2 text-sm font-geist text-brand-text/50">
          <li>
            <a href="/" className="hover:text-brand-blue transition-colors">Home</a>
          </li>
          <li className="flex items-center gap-2">
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-xs"></i>
            <span className="text-brand-text">Werkgebied</span>
          </li>
        </ol>
      </div>
    </nav>
  );
}

function SchemaMarkup() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Werkgebied Ramen Repair — Eindhoven en omgeving",
    description:
      "Ramen Repair Eindhoven werkt in alle wijken van Eindhoven en omliggende gemeenten. 48u service, 10 jaar garantie.",
    url: "https://ramen-repair-eindhoven.nl/wijken/",
    isPartOf: {
      "@type": "WebSite",
      name: "Ramen Repair Eindhoven",
      url: "https://ramen-repair-eindhoven.nl/",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://ramen-repair-eindhoven.nl/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Werkgebied",
        item: "https://ramen-repair-eindhoven.nl/wijken/",
      },
    ],
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Kozijn- en raamreparatie Eindhoven",
    provider: {
      "@type": "Organization",
      name: "Ramen Repair Eindhoven",
      url: "https://ramen-repair-eindhoven.nl/",
    },
    areaServed: [
      {
        "@type": "City",
        name: "Eindhoven",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Eindhoven",
          addressRegion: "Noord-Brabant",
          addressCountry: "NL",
        },
      },
      {
        "@type": "City",
        name: "Veldhoven",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Veldhoven",
          addressRegion: "Noord-Brabant",
          addressCountry: "NL",
        },
      },
      {
        "@type": "City",
        name: "Geldrop",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Geldrop",
          addressRegion: "Noord-Brabant",
          addressCountry: "NL",
        },
      },
    ],
    serviceType: [
      "Kozijnreparatie",
      "Raamreparatie",
      "Schuifpui reparatie",
      "Glas vervangen",
      "Slot- en cilindervervanging",
      "Velux en dakraam reparatie",
      "Inbraakschade reparatie",
      "Raamkruk vervangen",
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
    </>
  );
}

interface CardData {
  name: string;
  subtitle: string;
  description: string;
  href: string;
  comingSoon: boolean;
}

function WijkCard({ card, serviceLabel }: { card: CardData; serviceLabel: string }) {
  if (card.comingSoon) {
    return (
      <div className="bg-white rounded-xl p-6 md:p-8 border border-brand-border/60 opacity-60">
        <span className="font-geist text-xs font-semibold text-brand-yellow uppercase tracking-wider mb-2 block">
          {card.subtitle}
        </span>
        <h3 className="font-geist font-bold text-lg md:text-xl text-brand-text mb-3">
          {card.name}
        </h3>
        <p className="font-geist text-sm text-brand-text/50 italic">
          {card.description}
        </p>
        <span className="inline-flex items-center gap-1 mt-4 font-geist text-sm text-brand-text/40">
          <i className="ri-time-line w-4 h-4 flex items-center justify-center text-base"></i>
          Binnenkort beschikbaar
        </span>
      </div>
    );
  }

  const label = serviceLabel.replace("{name}", card.name);

  return (
    <a
      href={card.href}
      className="group bg-white rounded-xl p-6 md:p-8 border border-brand-border/40 hover:border-brand-yellow/50 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 block"
    >
      <span className="font-geist text-xs font-semibold text-brand-yellow uppercase tracking-wider mb-2 block">
        {card.subtitle}
      </span>
      <h3 className="font-geist font-bold text-lg md:text-xl text-brand-text mb-3 group-hover:text-brand-yellow transition-colors">
        {card.name}
      </h3>
      <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-4">
        {card.description}
      </p>
      <span className="inline-flex items-center gap-1 font-geist text-sm font-semibold text-brand-yellow group-hover:text-brand-text transition-colors">
        {label}
        <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-base group-hover:translate-x-1 transition-transform"></i>
      </span>
    </a>
  );
}

export default function Wijken() {
  const { t } = useTranslation("wijken");

  const cards = t("cards", { returnObjects: true }) as CardData[];
  const serviceLabel = t("serviceLabel");

  return (
    <main className="bg-white min-h-screen">
      <title>{t("seo.title")}</title>
      <meta name="description" content={t("seo.description")} />
      <meta property="og:title" content={t("seo.title")} />
      <meta property="og:description" content={t("seo.description")} />
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://ramen-repair-eindhoven.nl/wijken/" />
      <meta property="og:image" content="/og/og-wijken.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Ramen Repair Eindhoven - kozijn en raamreparatie in alle wijken" />
      <link rel="canonical" href="https://ramen-repair-eindhoven.nl/wijken/" />
      <link rel="alternate" hreflang="nl-NL" href="https://ramen-repair-eindhoven.nl/wijken/" />
      <link rel="alternate" hreflang="x-default" href="https://ramen-repair-eindhoven.nl/wijken/" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t("seo.title")} />
      <meta name="twitter:description" content={t("seo.description")} />
      <meta name="twitter:image" content="/og/og-wijken.jpg" />
      <SchemaMarkup />
      <TopBar />
      <Header />
      <Breadcrumb />

      {/* Hero */}
      <section className="bg-brand-text">
        <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-16 md:py-24 lg:py-32">
          <div className="max-w-3xl">
            <h1 className="font-geist font-bold text-3xl md:text-4xl lg:text-5xl text-white tracking-tight mb-5">
              {t("seo.h1")}
            </h1>
            <p className="font-geist text-base md:text-lg text-white/70 leading-relaxed mb-8 max-w-2xl">
              {t("hero.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <a
                href={t("hero.cta1Href")}
                className="inline-flex items-center justify-center gap-2 bg-brand-yellow text-brand-text font-geist font-semibold text-sm px-6 py-3.5 rounded-md hover:bg-brand-yellow/90 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line w-5 h-5 flex items-center justify-center text-base"></i>
                {t("hero.cta1")}
              </a>
              <a
                href={t("hero.cta2Href")}
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-geist font-medium text-sm px-6 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap"
              >
                <i className="ri-file-list-line w-5 h-5 flex items-center justify-center text-base"></i>
                {t("hero.cta2")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Cards Grid */}
      <section className="max-w-[1440px] mx-auto px-4 md:px-10 py-16 md:py-24">
        <div className="max-w-2xl mb-12 md:mb-16">
          <h2 className="font-geist font-bold text-2xl md:text-3xl lg:text-4xl text-brand-text tracking-tight mb-3">
            {t("gridTitle")}
          </h2>
          <p className="font-geist text-base md:text-lg text-brand-text/60 leading-relaxed">
            {t("gridSubtitle")}
          </p>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6"
        >
          {cards.map((card) => (
            <div id={card.comingSoon ? card.href.replace("#", "") : undefined} key={card.name}>
              <WijkCard card={card} serviceLabel={serviceLabel} />
            </div>
          ))}
        </div>
      </section>

      <Footer />
      <StickyMobile />
    </main>
  );
}